import React, { useEffect, useState } from 'react';

// assets
import { ICONS } from 'assets/icons';

// utils
import { Popover } from 'antd';

// helper
import { cn } from 'helper/cn';

// hooks
import useToggle from 'hooks/useToggle';

// constants
import { countryListApi } from 'modules/crm/api/Api';

// components
import SearchBar from '../searchBar/index';
import RadioButton from 'components/form/radio/RadioButton';

export default function CountryCode({
  value,
  onChange,
  className = '',
  modalClassName = '',
  expandable = true,
}) {
  const [selected, setSelected] = useState(null);
  const [searchKey, setSearchKey] = useState('');
  const [open, setOpen] = useToggle(false);
  const [countryList, setCountryList] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await countryListApi();
        if (response?.data) {
          setCountryList(response.data.data.map(country => ({
            id: country.id,
            label: country.name,
            value: `+${country.phone_code}`,
            shortName: country.alpha_2_code,
            flag: country.flag_emoji
          })));
        }
      } catch (error) {
        console.error('Error fetching countries:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCountries();
  }, []);

  useEffect(() => {
    if (typeof value === 'string') {
      // Try to find by the full format first (US (+1))
      const formatMatch = value.match(/([A-Z]{2}) \(\+([0-9]+)\)/);
      if (formatMatch) {
        const [_, shortName, code] = formatMatch;
        const country = countryList.find(c => 
          c.shortName === shortName && c.value === `+${code}`
        );
        if (country) {
          setSelected(country);
          return;
        }
      }
      // Fallback to direct value match
      const country = countryList.find(c => c.value === value);
      if (country) {
        setSelected(country);
      }
    } else if (value) {
      setSelected(value);
    }
  }, [value, countryList]);

  const getFlagEmoji = (countryCode) => {
    // Handles special cases for non-standard codes if needed
    // Otherwise, works for all ISO 3166-1 alpha-2 codes
    return countryCode
      .toUpperCase()
      .replace(/./g, char => 
        String.fromCodePoint(127397 + char.charCodeAt())
      );
  };

  const countryListWithFlags = countryList.map(country => ({
    ...country,
    flag: getFlagEmoji(country.shortName),
    displayValue: `${country.shortName} (${country.value})`
  }));

  return (
    <Popover
      onOpenChange={(open) => {
        setOpen(open);
        setSearchKey('');
      }}
      open={open}
      trigger={['click']}
      content={
        <div
          className={cn(
            'w-[80vw] sm:w-[60vw] md:w-[30vw] lg:w-[20vw] xl:w-[15vw] pl-2 sm:pl-[0.5vw] pb-2 sm:pb-[0.5vw] pt-2 sm:pt-[0.7vw] pr-1 sm:pr-[0.2vw]',
            modalClassName
          )}
        >
          <div className='h-auto min-h-[40px] sm:h-[5vh] pr-2 sm:pr-[0.5vw] mb-2 sm:mb-[0.4vw] border-b border-[var(--border-100)] pb-2'>
            <div className='relative w-full h-8 sm:h-7'>
              <input
                type='text'
                value={searchKey}
                onChange={(e) => setSearchKey(e.target.value)}
                placeholder='Search country...'
                className='w-full h-full pl-2 pr-8 text-sm border border-[var(--border-200)] rounded-[0.3vw] focus:outline-none focus:ring-0 focus:border-[var(--border-200)]'
              />
              {searchKey ? (
                <button
                  onClick={() => setSearchKey('')}
                  className='absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600'
                >
                  ✕
                </button>
              ) : (
                <div className='absolute right-2 top-1/2 -translate-y-1/2 text-gray-400'>
                  <svg
                    width='14'
                    height='14'
                    viewBox='0 0 14 14'
                    fill='none'
                    xmlns='http://www.w3.org/2000/svg'
                  >
                    <path
                      d='M6.33333 10.3333C8.5425 10.3333 10.3333 8.5425 10.3333 6.33333C10.3333 4.12417 8.5425 2.33333 6.33333 2.33333C4.12417 2.33333 2.33333 4.12417 2.33333 6.33333C2.33333 8.5425 4.12417 10.3333 6.33333 10.3333Z'
                      stroke='currentColor'
                      strokeWidth='1.5'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                    <path
                      d='M11.6667 11.6667L9.1 9.10001'
                      stroke='currentColor'
                      strokeWidth='1.5'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                  </svg>
                </div>
              )}
            </div>
          </div>
          <div
            className={`max-h-[50vh] sm:max-h-[35vh] pt-1 sm:pt-[0.3vw] overflow-y-auto listScroll pr-1 sm:pr-[0.3vw]`}
          >
            {loading ? (
                <div className='flex items-center justify-center h-[20vh]'>
                  <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500'></div>
                </div>
              ) : countryListWithFlags?.map((l, i) => {
              const codefilter = l?.value.includes(searchKey);
              const name = l?.label
                .toLocaleLowerCase()
                .includes(searchKey.toLocaleLowerCase());
              const shortNameFilter = l?.shortName
                .toLocaleLowerCase()
                .includes(searchKey.toLocaleLowerCase());
              return (
                (codefilter || name || shortNameFilter) && (
                  <div
                    key={`country-code-for-${l.shortName}`}
                    className='border-b-[0.08vw] border-[var(--border-50)] cursor-pointer last:border-b-0'
                    onClick={() => {
                      setSelected(l);
                      setOpen(false);
                      setSearchKey('');
                      if (onChange) {
                        onChange(l);
                        setSelected(l);
                      }
                    }}
                  >
                    <div
                      className={`h-auto flex items-center justify-between px-2 sm:px-[0.5vw] rounded-md sm:rounded-[0.5vw] py-1 sm:py-[0.3vw] my-1 sm:my-[0.3vw] hover:bg-slate-100`}
                    >
                      <div className=''>
                        <div className='text-[10px] font-[600] text-[#898E99]'>
                          {l?.label}
                        </div>
                        <div className='flex items-center gap-[0.7vw] text-[10px] font-[600]'>
                          <div>{l?.displayValue}</div>
                        </div>
                      </div>
                      {selected?.id === l?.id && (
                        <div className='mt-1 sm:mt-[0.5vw]'>
                          <RadioButton selected={true} />
                        </div>
                      )}
                    </div>
                  </div>
                )
              );
            })}
            {countryListWithFlags.filter(l => {
              const codefilter = l?.value.includes(searchKey);
              const name = l?.label.toLowerCase().includes(searchKey.toLowerCase());
              const shortNameFilter = l?.shortName.toLowerCase().includes(searchKey.toLowerCase());
              return codefilter || name || shortNameFilter;
            }).length === 0 && searchKey && (
              <div className='text-center py-2 text-gray-500 text-sm'>
                No countries found
              </div>
            )}
          </div>
        </div>
      }
    >
      <div
        className={cn(
          `w-full h-full border border-[0.08vw] flex items-center justify-between text-[10px] font-[600] rounded-md sm:rounded-[0.4vw] px-2 sm:px-[0.5vw] py-1 sm:py-[0.2vw] cursor-pointer`,
          className
        )}
      >
        <div className="flex-1 whitespace-nowrap overflow-hidden text-ellipsis">
          {selected ? `${selected.shortName} (${selected.value})` : value || 'Select Country'}
        </div>
        <div>
          <img
            src={ICONS?.SelectDropdown}
            alt='icons'
            className={`transition-transform ease-linear duration-200 ${
              open ? 'rotate-180' : 'rotate-0'
            }`}
          />
        </div>
      </div>
    </Popover>
  );
}
